import React, { useMemo, useState } from 'react'
import { AgentiMonthlyStatsState, AgentiStatsState } from './localstate'
import { useQsDataTable as useDataTable } from 'react-use-datatable/react'
import CircularProgress from '@material-ui/core/CircularProgress'
import { Button } from 'reactstrap'
import moment from 'moment'
import Column from '../../components/Column'
import { useHistory } from 'react-router-dom'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import ReactSelect from 'react-select'
import { useRunRj } from 'react-rocketjump'
import * as XLSX from 'xlsx'
import { AgentiState } from '../AdminPreventiviApp/localstate'
import { PERMISSIONS, USER } from '../../django'
import classNames from 'classnames'
import {
  DifferenceCell,
  EuroCell,
  HighlightCell,
  MonthlyProgressCell,
  StatsMonthRangeFilter,
  StatsTypeToggle,
  TotalBadgeCell,
  formatStatsEuro,
  formatStatsPercentGap,
  formatStatsPercentValue,
  getDifferenceColor,
} from './Stats/components'

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0
  }
  return Number(String(value).replace(',', '.'))
}

const getAgenteName = (agente) =>
  `${agente?.user_data?.first_name || ''} ${
    agente?.user_data?.last_name || ''
  }`.trim()

const getDateRangeSuffix = (filters) =>
  `${filters.data_inizio || 'inizio'}_${filters.data_fine || 'fine'}`

const writeWorkbook = (filename, sheets) => {
  const workbook = XLSX.utils.book_new()
  sheets.forEach(({ name, rows, widths }) => {
    const worksheet = XLSX.utils.json_to_sheet(rows)
    if (widths) {
      worksheet['!cols'] = widths.map((wch) => ({ wch }))
    }
    XLSX.utils.book_append_sheet(workbook, worksheet, name)
  })
  XLSX.writeFile(workbook, filename)
}

const truncateText = (context, text, maxWidth) => {
  const value = String(text || '')
  if (context.measureText(value).width <= maxWidth) return value

  let truncated = value
  while (
    truncated.length > 0 &&
    context.measureText(`${truncated}...`).width > maxWidth
  ) {
    truncated = truncated.slice(0, -1)
  }
  return `${truncated}...`
}

const drawText = (context, text, x, y, maxWidth, options = {}) => {
  context.font = options.font || '12px Arial'
  context.fillStyle = options.color || '#212529'
  context.textAlign = options.align || 'left'
  context.textBaseline = 'top'
  context.fillText(truncateText(context, text, maxWidth), x, y)
}

const drawMonthlyCell = (context, stats, x, y, width, height) => {
  context.fillStyle =
    stats?.differenza_netto_budget >= 100 ? '#CFF5C9' : '#ffffff'
  context.fillRect(x, y, width, height)
  context.strokeStyle = '#d6dbe1'
  context.strokeRect(x, y, width, height)

  if (!stats) {
    drawText(context, '-', x + width / 2, y + 24, width - 16, {
      align: 'center',
      color: '#6c757d',
    })
    return
  }

  const progress = toNumber(stats.differenza_netto_budget)
  drawText(context, formatStatsPercentGap(progress), x + 8, y + 8, 70, {
    font: 'bold 13px Arial',
  })
  drawText(
    context,
    `${stats.numero_ordini || 0} comm.`,
    x + width - 8,
    y + 9,
    70,
    {
      align: 'right',
      color: '#6c757d',
      font: '11px Arial',
    }
  )
  drawText(
    context,
    `${formatStatsEuro(stats.netto_serramenti)} / ${formatStatsEuro(stats.budget_agente)}`,
    x + 8,
    y + 29,
    width - 16,
    { color: '#6c757d', font: '11px Arial' }
  )

  const barX = x + 8
  const barY = y + height - 15
  const barWidth = width - 16
  context.fillStyle = '#D7E7F8'
  context.fillRect(barX, barY, barWidth, 5)
  context.fillStyle = getDifferenceColor(progress)
  context.fillRect(
    barX,
    barY,
    Math.max(0, Math.min(barWidth, (barWidth * progress) / 100)),
    5
  )
}

const downloadMonthlyStatsAsPng = (monthlyStats, filters, filename) => {
  if (!monthlyStats) return

  const rows = [
    { label: 'Totale', mesi: monthlyStats.totals || {}, total: true },
    ...monthlyStats.results.map((agente) => ({
      label: getAgenteName(agente),
      mesi: agente.mesi || {},
    })),
  ]
  const firstColWidth = 240
  const monthColWidth = 220
  const titleHeight = 72
  const headerHeight = 42
  const rowHeight = 68
  const padding = 24
  const width =
    padding * 2 + firstColWidth + monthColWidth * monthlyStats.months.length
  const height =
    padding * 2 + titleHeight + headerHeight + rowHeight * rows.length
  const scale = 2
  const canvas = document.createElement('canvas')
  canvas.width = width * scale
  canvas.height = height * scale
  const context = canvas.getContext('2d')
  context.scale(scale, scale)
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, width, height)

  drawText(context, 'Statistiche agenti mensili', padding, padding, 420, {
    font: 'bold 22px Arial',
  })
  drawText(
    context,
    `Periodo ${filters.data_inizio} / ${filters.data_fine}`,
    padding,
    padding + 32,
    420,
    { color: '#6c757d', font: '13px Arial' }
  )

  const tableY = padding + titleHeight
  context.fillStyle = '#f3f6f8'
  context.fillRect(padding, tableY, width - padding * 2, headerHeight)
  context.strokeStyle = '#d6dbe1'
  context.strokeRect(padding, tableY, firstColWidth, headerHeight)
  drawText(context, 'Agente', padding + 8, tableY + 13, firstColWidth - 16, {
    font: 'bold 12px Arial',
  })
  monthlyStats.months.forEach((month, index) => {
    const x = padding + firstColWidth + index * monthColWidth
    context.strokeStyle = '#d6dbe1'
    context.strokeRect(x, tableY, monthColWidth, headerHeight)
    drawText(
      context,
      month.label,
      x + monthColWidth / 2,
      tableY + 13,
      monthColWidth - 16,
      {
        align: 'center',
        font: 'bold 12px Arial',
      }
    )
  })

  rows.forEach((row, rowIndex) => {
    const y = tableY + headerHeight + rowIndex * rowHeight
    context.fillStyle = row.total ? '#f8f9fa' : '#ffffff'
    context.fillRect(padding, y, firstColWidth, rowHeight)
    context.strokeStyle = '#d6dbe1'
    context.strokeRect(padding, y, firstColWidth, rowHeight)
    drawText(context, row.label, padding + 8, y + 25, firstColWidth - 16, {
      font: row.total ? 'bold 12px Arial' : '12px Arial',
    })
    monthlyStats.months.forEach((month, index) => {
      drawMonthlyCell(
        context,
        row.mesi?.[month.key],
        padding + firstColWidth + index * monthColWidth,
        y,
        monthColWidth,
        rowHeight
      )
    })
  })

  const link = document.createElement('a')
  link.download = filename
  link.href = canvas.toDataURL('image/png')
  link.click()
}

const buildAgentiExportRows = (agenti = []) =>
  agenti.map((agente) => ({
    Agente: getAgenteName(agente),
    'N° clienti': agente.numero_clienti || 0,
    'N° commesse': agente.numero_ordini || 0,
    'N° serramenti': agente.numero_serramenti || 0,
    Listino: toNumber(agente.listino_serramenti),
    'Sconto %': toNumber(agente.sconto_serramenti),
    'Media commesse': toNumber(agente.media_ordini),
    'Media € serramento': toNumber(agente.media_serramenti),
    Netto: toNumber(agente.netto_serramenti),
    Budget: toNumber(agente.budget_agente),
    'Differenza %': toNumber(agente.differenza_netto_budget) - 100,
    'Differenza al budget': toNumber(agente.differenza_netto_budget_euro),
  }))

const buildTotalsExportRow = (totals) => ({
  Agente: 'Totale',
  'N° clienti': totals?.totale_clienti || 0,
  'N° commesse': totals?.totale_ordini || 0,
  'N° serramenti': totals?.totale_serramenti || 0,
  Listino: toNumber(totals?.totale_listino),
  'Sconto %': toNumber(totals?.totale_sconto),
  'Media commesse': toNumber(totals?.totale_media_ordini),
  'Media € serramento': toNumber(totals?.totale_media_serramenti),
  Netto: toNumber(totals?.totale_netto),
  Budget: toNumber(totals?.totale_budget),
  'Differenza %': toNumber(totals?.totale_differenza_netto_budget) - 100,
  'Differenza al budget': toNumber(totals?.totale_differenza_netto_budget_euro),
})

const buildMonthlyExportRows = (monthlyStats) => {
  if (!monthlyStats) return []

  const buildRow = (label, mesi = {}) => {
    const row = { Agente: label }
    monthlyStats.months.forEach((month) => {
      const stats = mesi[month.key] || {}
      row[`${month.label} Netto`] = toNumber(stats.netto_serramenti)
      row[`${month.label} Budget`] = toNumber(stats.budget_agente)
      row[`${month.label} Differenza %`] =
        toNumber(stats.differenza_netto_budget) - 100
      row[`${month.label} Differenza al budget`] = toNumber(
        stats.differenza_netto_budget_euro
      )
      row[`${month.label} N° commesse`] = stats.numero_ordini || 0
    })
    return row
  }

  return [
    buildRow('Totale', monthlyStats.totals),
    ...monthlyStats.results.map((agente) =>
      buildRow(getAgenteName(agente), agente.mesi)
    ),
  ]
}

export default function StatsAgenti() {
  const [
    // Data State
    { agenti, loading, totals },
    // Table state
    { ...filters },
    {
      // DT Actions
      onFilterChangeDebounced,
      onFilterChange,
      onFiltersChange,
    },
  ] = useDataTable(AgentiStatsState, () => ({
    search: '',
    agenti: [],
    tipo_agente: '',
    page_size: 10000,
    ordering: 'user__first_name',
    data_inizio: moment()
      .subtract(1, 'month')
      .startOf('month')
      .format('YYYY-MM-DD'),
    data_fine: moment()
      .subtract(1, 'month')
      .endOf('month')
      .format('YYYY-MM-DD'),
  }))

  const columnProps = {
    value: filters.ordering,
    onChange: (value) => onFilterChange(value, 'ordering'),
  }

  const history = useHistory()

  const [typeStat, setTypeStat] = useState('table')

  const [{ data: agentiState }] = useRunRj(AgentiState)

  const chartAgenti = useMemo(
    () =>
      (agenti || []).map((agente) => ({
        ...agente,
        differenza_netto_budget_gap:
          toNumber(agente.differenza_netto_budget) - 100,
      })),
    [agenti]
  )

  const monthlyFilters = useMemo(
    () => ({
      search: filters.search,
      agenti: filters.agenti,
      tipo_agente: filters.tipo_agente,
      ordering: filters.ordering,
      data_inizio: filters.data_inizio,
      data_fine: filters.data_fine,
    }),
    [
      filters.search,
      filters.agenti,
      filters.tipo_agente,
      filters.ordering,
      filters.data_inizio,
      filters.data_fine,
    ]
  )

  const [{ data: monthlyStats, loading: monthlyLoading }] = useRunRj(
    AgentiMonthlyStatsState,
    [typeStat === 'monthly' ? monthlyFilters : null]
  )

  const agentiOptions = useMemo(() => {
    if (!agentiState) return []
    const idAgente = USER.agente?.id
    if (PERMISSIONS.isCapoArea) {
      const agi = agentiState
        .filter((agente) => agente?.capo_area === idAgente)
        .map((agente) => ({
          value: agente.id,
          label: agente.user.first_name + ' ' + agente.user.last_name,
        }))
      return agi.sort((a, b) => {
        if (a.label < b.label) return -1
        if (a.label > b.label) return 1
        return 0
      })
    }

    // check if user is capo   area

    // return all agenti

    const agi = agentiState.map((agente) => ({
      value: agente.id,
      label: agente.user.first_name + ' ' + agente.user.last_name,
    }))
    // return sorted agenti
    return agi.sort((a, b) => {
      if (a.label < b.label) return -1
      if (a.label > b.label) return 1
      return 0
    })
  }, [agentiState])

  const selectedAgenti = useMemo(() => {
    if (!filters.agenti || !agentiState) {
      return []
    }
    if (typeof filters.agenti === 'string') {
      return filters.agenti.split(',').map((id) => ({
        value: id,
        label:
          agentiState.find((agente) => agente.id === Number(id)).user
            .first_name +
          ' ' +
          agentiState.find((agente) => agente.id === Number(id)).user.last_name,
      }))
    } else {
      return filters.agenti.map((id) => ({
        value: id,
        label:
          agentiState.find((agente) => agente.id === Number(id)).user
            .first_name +
          ' ' +
          agentiState.find((agente) => agente.id === Number(id)).user.last_name,
      }))
    }
  }, [filters.agenti, agentiState])

  const agentiForQs = useMemo(() => {
    if (!filters.agenti || !agenti) {
      return ''
    }
    if (typeof filters.agenti === 'string') {
      return '&agenti=' + filters.agenti
    } else {
      return filters.agenti.map((id) => '&agenti=' + id).join('')
    }
  }, [filters.agenti, agenti])

  const handleExportExcel = () => {
    const suffix = getDateRangeSuffix(filters)

    if (typeStat === 'monthly') {
      writeWorkbook(`statistiche_agenti_mensili_${suffix}.xlsx`, [
        {
          name: 'Mensile',
          rows: buildMonthlyExportRows(monthlyStats),
          widths: [
            28,
            ...monthlyStats.months.flatMap(() => [16, 16, 18, 18, 14]),
          ],
        },
      ])
      return
    }

    const rows = [
      buildTotalsExportRow(totals),
      ...buildAgentiExportRows(agenti),
    ]

    if (typeStat === 'chart') {
      writeWorkbook(`statistiche_agenti_grafico_${suffix}.xlsx`, [
        {
          name: 'Avanzamento',
          rows: rows.map((row) => ({
            Agente: row.Agente,
            Netto: row.Netto,
            Budget: row.Budget,
            'Differenza %': row['Differenza %'],
            'Differenza al budget': row['Differenza al budget'],
            'N° commesse': row['N° commesse'],
          })),
          widths: [28, 16, 16, 18, 18, 14],
        },
      ])
      return
    }

    writeWorkbook(`statistiche_agenti_tabella_${suffix}.xlsx`, [
      {
        name: 'Statistiche',
        rows,
        widths: [28, 12, 14, 14, 16, 12, 16, 18, 16, 16, 18, 18],
      },
    ])
  }

  const handleExportMonthlyPng = () => {
    downloadMonthlyStatsAsPng(
      monthlyStats,
      filters,
      `statistiche_agenti_mensili_${getDateRangeSuffix(filters)}.png`
    )
  }

  const canExportExcel =
    typeStat === 'monthly'
      ? monthlyStats && !monthlyLoading
      : agenti && !loading

  return (
    <div className="container-fluid">
      {agenti === null && loading && (
        <div className="text-center mt-4">
          <CircularProgress color="primary" />
        </div>
      )}
      {agenti && (
        <>
          <h3 className="mt-2">Statistiche agenti</h3>
          <div className="row mt-4 align-items-center stats-filter-row">
            <div className="col-md-3 stats-filter-col">
              <StatsMonthRangeFilter
                filters={filters}
                onFiltersChange={onFiltersChange}
              />
            </div>
            <div className="col-md-2 stats-filter-col">
              <select
                className="form-control"
                value={filters.tipo_agente}
                onChange={(e) =>
                  onFiltersChange({ tipo_agente: e.target.value })
                }
              >
                <option value={''}>Filtra per tipo agente</option>
                <option value={'B2C'}>B2C</option>
                <option value={'B2B'}>B2B</option>
                <option value={'B2C*s'}>B2C*s</option>
              </select>
            </div>

            <div className="col-md-5 stats-filter-col">
              <ReactSelect
                name="agenti"
                isMulti
                placeholder={'Filtra per agenti'}
                options={agentiOptions}
                classNamePrefix="multi-select"
                hideSelectedOptions={false}
                isOptionSelected={(option) =>
                  filters.agenti && filters.agenti.includes(option.value)
                }
                value={selectedAgenti}
                className="stats-multi-select select-multi-options text-nowrap w-100"
                onChange={(newValues) => {
                  onFiltersChange({
                    ...filters,
                    agenti: newValues ? newValues.map((v) => v.value) : [],
                  })
                }}
              />
            </div>
            <div className="col-md-2 stats-filter-col">
              {/* button for reset */}
              <Button
                onClick={() =>
                  onFiltersChange({
                    tipo_agente: '',
                    agenti: [],
                    data_inizio: moment()
                      .subtract(1, 'month')
                      .startOf('month')
                      .format('YYYY-MM-DD'),
                    data_fine: moment()
                      .subtract(1, 'month')
                      .endOf('month')
                      .format('YYYY-MM-DD'),
                  })
                }
                size="sm"
                outline
                color="secondary"
              >
                Reset
              </Button>
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-md-12">
              <div className="border-bottom d-flex align-items-center w-100">
                <div
                  className="pointer"
                  onClick={() =>
                    history.push(
                      '/statistiche-clienti?data_inizio=' +
                        filters.data_inizio +
                        '&data_fine=' +
                        filters.data_fine +
                        agentiForQs
                    )
                  }
                >
                  Clienti
                </div>
                <div
                  className={classNames(
                    'font-weight-bold border-bottom border-primary pointer ml-4'
                  )}
                  onClick={() =>
                    history.push(
                      '/statistiche-agenti?data_inizio=' +
                        filters.data_inizio +
                        '&data_fine=' +
                        filters.data_fine +
                        agentiForQs
                    )
                  }
                >
                  Agenti
                </div>
              </div>
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-md-12">
              <div className="stats-actions-bar d-flex align-items-center justify-content-between">
                <StatsTypeToggle
                  value={typeStat}
                  onChange={setTypeStat}
                  options={[
                    { value: 'table', label: 'Tabella' },
                    { value: 'chart', label: 'Grafico' },
                    { value: 'monthly', label: 'Mensile' },
                  ]}
                />
                <div className="stats-export-actions d-flex justify-content-end">
                  <Button
                    color="success"
                    size="sm"
                    disabled={!canExportExcel}
                    onClick={handleExportExcel}
                  >
                    <i className="fas fa-file-excel mr-2"></i>
                    Esporta Excel
                  </Button>
                  {typeStat === 'monthly' && (
                    <Button
                      color="primary"
                      size="sm"
                      disabled={!monthlyStats || monthlyLoading}
                      onClick={handleExportMonthlyPng}
                    >
                      <i className="fas fa-image mr-2"></i>
                      PNG mensile
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
          {typeStat === 'table' && (
            <div className="table-responsive stats-table-responsive">
              <table className="table table-bordered mt-2 stats-scroll-table">
                <thead>
                  <tr>
                    <td>
                      <input
                        name="search"
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={'Ricerca libera'}
                        value={filters && filters.search}
                        onChange={onFilterChangeDebounced}
                      />
                    </td>

                    <TotalBadgeCell
                      className="text-center"
                      value={totals?.totale_clienti}
                    />
                    <TotalBadgeCell
                      className="text-center"
                      value={totals?.totale_ordini}
                    />
                    <TotalBadgeCell
                      className="text-center"
                      value={totals?.totale_serramenti}
                    />
                    <TotalBadgeCell
                      nowrap
                      value={totals?.totale_listino}
                      format={formatStatsEuro}
                    />
                    <TotalBadgeCell
                      value={totals?.totale_sconto}
                      format={(value) => `${value.toFixed(2)} %`}
                    />
                    <TotalBadgeCell
                      nowrap
                      value={totals?.totale_media_ordini}
                      format={formatStatsEuro}
                    />
                    <TotalBadgeCell
                      nowrap
                      value={totals?.totale_media_serramenti}
                      format={formatStatsEuro}
                    />
                    <TotalBadgeCell
                      nowrap
                      value={totals?.totale_netto}
                      format={formatStatsEuro}
                    />
                    <TotalBadgeCell
                      nowrap
                      value={totals?.totale_budget}
                      format={formatStatsEuro}
                    />
                    <TotalBadgeCell
                      value={totals?.totale_differenza_netto_budget}
                      format={formatStatsPercentGap}
                    />
                    <TotalBadgeCell
                      nowrap
                      value={totals?.totale_differenza_netto_budget_euro}
                      format={formatStatsEuro}
                    />
                  </tr>
                  <tr>
                    <Column {...columnProps} field="user__first_name">
                      Nome
                    </Column>
                    <Column {...columnProps} field="numero_clienti">
                      N° clienti
                    </Column>
                    <Column {...columnProps} field="numero_ordini">
                      N° comm.
                    </Column>
                    <Column {...columnProps} field="numero_serramenti">
                      N° serr.
                    </Column>
                    <Column {...columnProps} field="listino_serramenti">
                      Listino
                    </Column>
                    <Column {...columnProps} field="sconto_serramenti">
                      Sconto
                    </Column>
                    <Column {...columnProps} field="media_ordini">
                      Media comm.
                    </Column>
                    <Column {...columnProps} field="media_serramenti">
                      Media serr.
                    </Column>
                    <Column {...columnProps} field="netto_serramenti">
                      Netto
                    </Column>
                    <Column {...columnProps} field="budget_agente">
                      Budget
                    </Column>
                    <Column {...columnProps} field="differenza_netto_budget">
                      Diff. %
                    </Column>
                    <Column
                      {...columnProps}
                      field="differenza_netto_budget_euro"
                    >
                      Diff. budget
                    </Column>
                  </tr>
                </thead>
                <tbody className="stats-table">
                  {agenti.map((agente) => (
                    <tr key={agente.id}>
                      <HighlightCell active={agente.differenza_netto_budget}>
                        {agente?.user_data?.first_name}{' '}
                        {agente?.user_data?.last_name}
                      </HighlightCell>
                      <HighlightCell active={agente.differenza_netto_budget}>
                        {agente.numero_clienti}
                      </HighlightCell>
                      <HighlightCell active={agente.differenza_netto_budget}>
                        {agente.numero_ordini}
                      </HighlightCell>
                      <HighlightCell active={agente.differenza_netto_budget}>
                        {agente.numero_serramenti}
                      </HighlightCell>
                      <EuroCell
                        active={agente.differenza_netto_budget}
                        value={agente.listino_serramenti}
                      />
                      <HighlightCell active={agente.differenza_netto_budget}>
                        {agente.sconto_serramenti} %
                      </HighlightCell>
                      <EuroCell
                        active={agente.differenza_netto_budget}
                        value={agente.media_ordini}
                      />
                      <EuroCell
                        active={agente.differenza_netto_budget}
                        value={agente.media_serramenti}
                      />
                      <EuroCell
                        active={agente.differenza_netto_budget}
                        value={agente.netto_serramenti}
                      />
                      <EuroCell
                        active={agente.differenza_netto_budget}
                        value={agente.budget_agente}
                      />
                      <DifferenceCell
                        active={agente.differenza_netto_budget}
                        value={agente.differenza_netto_budget}
                      />
                      <EuroCell
                        active={agente.differenza_netto_budget}
                        value={agente.differenza_netto_budget_euro}
                      />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {typeStat === 'chart' && (
            <ResponsiveContainer width="100%" height={650} className={'mt-4'}>
              <BarChart
                layout="vertical"
                data={chartAgenti}
                margin={{ top: 5, right: 30, left: 80, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  dataKey="differenza_netto_budget_gap"
                  tickFormatter={formatStatsPercentValue}
                />
                <YAxis
                  type="category"
                  dataKey="user_data.first_name"
                  tickFormatter={(value) =>
                    value +
                    ' ' +
                    chartAgenti.find((a) => a.user_data.first_name === value)
                      ?.user_data.last_name
                  }
                />
                <Tooltip
                  formatter={(value) => formatStatsPercentValue(value)}
                  labelFormatter={(value) =>
                    value +
                    ' ' +
                    chartAgenti.find((a) => a.user_data.first_name === value)
                      ?.user_data.last_name
                  }
                />
                {/* <Legend /> */}
                <Bar dataKey="differenza_netto_budget_gap">
                  {chartAgenti.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getDifferenceColor(entry.differenza_netto_budget)}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          {typeStat === 'monthly' && (
            <div className="table-responsive stats-table-responsive mt-2">
              {monthlyLoading && (
                <div className="text-center mt-4">
                  <CircularProgress color="primary" />
                </div>
              )}
              {!monthlyLoading && monthlyStats && (
                <div className="stats-monthly-print-report">
                  <div className="stats-monthly-print-header">
                    <div>
                      <h4 className="stats-monthly-print-title">
                        Statistiche agenti mensili
                      </h4>
                      <div className="stats-monthly-print-subtitle">
                        Periodo {filters.data_inizio} / {filters.data_fine}
                      </div>
                    </div>
                  </div>
                  <table className="table table-bordered stats-monthly-table">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 220 }}>Agente</th>
                        {monthlyStats.months.map((month) => (
                          <th
                            key={month.key}
                            className="text-center"
                            style={{ minWidth: 190 }}
                          >
                            {month.label}
                          </th>
                        ))}
                      </tr>
                      <tr>
                        <TotalBadgeCell>Totale</TotalBadgeCell>
                        {monthlyStats.months.map((month) => (
                          <MonthlyProgressCell
                            key={month.key}
                            stats={monthlyStats.totals?.[month.key]}
                          />
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyStats.results.length === 0 && (
                        <tr>
                          <td
                            colSpan={monthlyStats.months.length + 1}
                            className="text-center text-muted"
                          >
                            Nessun dato per il periodo selezionato
                          </td>
                        </tr>
                      )}
                      {monthlyStats.results.map((agente) => (
                        <tr key={agente.id}>
                          <th className="align-middle">
                            {agente.user_data.first_name}{' '}
                            {agente.user_data.last_name}
                          </th>
                          {monthlyStats.months.map((month) => (
                            <MonthlyProgressCell
                              key={month.key}
                              stats={agente.mesi?.[month.key]}
                            />
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
